import { isEqual } from 'lodash';
import type { AdobeGateway } from './AdobeGateway';
import { TimeTable, type TimeTableEntry, type TimeTableInformationEntry } from './TimeTable';
import type { AdobeProgramTrackingFragment } from '../dataProviders/__generated__/Tracking';
import { BasicAdobePlaybackHelper } from './BasicAdobePlaybackHelper';
import type { UseTrackingMappedMedia } from '../../plugins/Tracking';
import {
  type UnifiedTrackingMachineAdBeginEvent,
  type UnifiedTrackingMachineAdBreakBeginEvent,
  UnifiedTrackingMachineLoadEvent,
  type UnifiedTrackingMachineTimeUpdateEvent,
} from '../tracking/UnifiedTrackingMachine';
import { getMediaObject, MediaHeartbeatEvents } from './AdobeTrackingMachine';
import { createAdBreakObject } from '../../services/Adobe/functions/createAdBreakObject';
import { createAdObject } from '../../services/Adobe/functions/createAdObject';
import type { AdobeTrackingStrategy } from './AdobeTrackingStrategy';
import { PlaybackInitTrackingTypes } from '../../types';
import type { AdobeMetadata } from './types';

export class AdobeLiveTrackingStrategy implements AdobeTrackingStrategy {
  private gateway: AdobeGateway;
  private playerName: string;
  private timeTable: TimeTable<AdobeProgramTrackingFragment>;
  private currentBlock: TimeTableInformationEntry<AdobeProgramTrackingFragment> | undefined =
    undefined;

  private playbackHelper: BasicAdobePlaybackHelper;
  private isInAd: boolean;
  private isInAdBreak: boolean;
  private lastSessionStartData: AdobeProgramTrackingFragment | undefined = undefined;
  private media: UseTrackingMappedMedia;
  private loadEvent: UnifiedTrackingMachineLoadEvent;
  private initiationType: PlaybackInitTrackingTypes;
  private epgUpdateType: string;
  private active: AdobeProgramTrackingFragment | null | undefined;

  constructor(adobeGateway: AdobeGateway, playerName: string) {
    this.gateway = adobeGateway;
    this.playerName = playerName;
    this.playbackHelper = new BasicAdobePlaybackHelper(adobeGateway);
  }

  onLoad(event: UnifiedTrackingMachineLoadEvent) {
    this.media = event.value.media;
    this.initiationType = this.media.playback.tracking.adobe?.videoInfo['video.initiationtype'];

    this.loadEvent = event;

    this.timeTable = new TimeTable<AdobeProgramTrackingFragment>(
      event.value.media.metadata.isStartOver ?? false,
      {
        data: {
          __typename: 'DetailedAdobe',
          ...event.value.media.playback!.tracking!.adobe!,
          startOverAvailable: false,
        },
      }
    );
  }

  onAdBegin(_event: UnifiedTrackingMachineAdBeginEvent): void {}

  onAdBreakBegin(_event: UnifiedTrackingMachineAdBreakBeginEvent): void {}

  onAdBreakEnd(): void {}

  onAdEnd(): void {}

  onTrackingEntries(
    entries: TimeTableEntry<AdobeProgramTrackingFragment>[],
    epgUpdateType: string,
    active: AdobeProgramTrackingFragment | null | undefined
  ): void {
    this.timeTable.addEntries(...entries);
    this.epgUpdateType = epgUpdateType;
    this.active = active;
  }

  onTimeUpdate(event: UnifiedTrackingMachineTimeUpdateEvent): void {
    const { currentTime } = event.value;
    if (currentTime <= 0) return;

    // On live we get a currentTime that we need to throttle to only update each second
    if (this.currentBlock === undefined) {
      this.currentBlock = this.findCurrentBlock(currentTime);
      this.handleSwitchingToNewBlock(this.currentBlock, currentTime, true);
    } else {
      const newBlock = this.findCurrentBlock(currentTime);
      if (newBlock && !this.isSameBlock(this.currentBlock, newBlock)) {
        this.currentBlock = newBlock;

        this.handleSwitchingToNewBlock(this.currentBlock, currentTime);
      }
    }

    this.updatePlayhead(currentTime);
    this.playbackHelper.sendQualityOfExperience(event.value);
  }

  private updatePlayhead(currentTime: number) {
    const start = this.currentBlock?.data.start ?? currentTime * 1000;
    const playhead = Math.floor(currentTime - start / 1000);
    this.playbackHelper.updatePlayhead(playhead);
  }

  private handleSwitchingToNewBlock(
    block: TimeTableInformationEntry<AdobeProgramTrackingFragment>,
    currentTime: number,
    isFirst: boolean = false
  ) {
    const initiationType = this.getInitiationType(isFirst);

    const data = block.parent ?? block.data;

    if (this.isInAd) {
      this.gateway.trackEvent(MediaHeartbeatEvents.adComplete);
      this.isInAd = false;
    }

    if (this.isInAdBreak && !block.parent) {
      this.gateway.trackEvent(MediaHeartbeatEvents.adBreakComplete);
      this.isInAdBreak = false;
    }

    if (isEqual(this.lastSessionStartData, data) === false) {
      if (this.lastSessionStartData) {
        this.gateway.trackComplete();
        this.gateway.trackSessionEnd();
        this.playbackHelper.isPlaying = false;
      }

      this.lastSessionStartData = data;

      const metadata = this.getMetadata(this.loadEvent, block, isFirst);

      this.gateway.trackSessionStart(
        getMediaObject({ title: data.title!, id: data.id!, duration: data.duration! }, this.media),
        {
          ...metadata,
          'video.title': data.title,
          'video.streamingtype': 'Segment of program',
          'video.id': data.id,
          'video.initiationtype': initiationType,
          'video.fallback': block.isFallback ? 'true' : 'false',
        }
      );
      this.playbackHelper.isInSession = true;
      this.updatePlayhead(currentTime);
    }

    if (block.parent) {
      if (!this.isInAdBreak) {
        this.gateway.trackEvent(
          MediaHeartbeatEvents.adBreakStart,
          createAdBreakObject('pre-roll', 1, currentTime)
        );
        this.isInAdBreak = true;
      }
      this.gateway.trackEvent(
        MediaHeartbeatEvents.adStart,
        createAdObject(
          block.data.title!,
          block.data.title!,
          this.timeTable.getAdIndex(block),
          block.data.duration!,
          this.playerName
        )
      );
      this.isInAd = true;
    }
  }

  private getInitiationType(isFirstProgramBlockInSession: boolean) {
    if (this.media.metadata.isStartOver) {
      return this.initiationType ?? 'timeshifted';
    }

    // If this is not the first program in the session, we should send "auto next linear"
    return isFirstProgramBlockInSession ? this.initiationType ?? 'manual' : 'auto next linear';
  }

  private findCurrentBlock(
    currentTime: number
  ): TimeTableInformationEntry<AdobeProgramTrackingFragment> {
    if (this.epgUpdateType === 'ACTIVE' && this.active) {
      return {
        data: this.active,
      };
    }
    return this.timeTable.getCurrentEntry(new Date(currentTime * 1000));
  }

  private isSameBlock(
    currentBlock: TimeTableInformationEntry<AdobeProgramTrackingFragment>,
    newBlock: TimeTableInformationEntry<AdobeProgramTrackingFragment>
  ) {
    return (
      currentBlock.data.title === newBlock.data.title && currentBlock.data.id === newBlock.data.id
    );
  }

  private getMetadata(
    event: UnifiedTrackingMachineLoadEvent,
    block: TimeTableInformationEntry<AdobeProgramTrackingFragment>,
    isFirst: boolean
  ): AdobeMetadata {
    const basicMetadata = this.playbackHelper.getBasicMetadata(event);

    const { media } = event.value;
    const { videoInfo } = media.playback.tracking.adobe!;
    const { duration } = block.data;

    const trackTimeshift = getTimeshiftInformation(
      videoInfo['video.initiationtype'] as PlaybackInitTrackingTypes,
      duration ?? 0,
      {
        liveEdge: event.value.media.playback.tracking.adobe?.custom?.liveEdge ?? 0,
        startTime: event.value.media.playback.tracking.adobe?.custom?.startTime ?? 0,
      }
    );

    // We should not include icid on other than the first program in the session
    if (!isFirst) {
      const icidKeys = Object.keys(basicMetadata).filter(key =>
        key.startsWith('content.icid')
      ) as (keyof AdobeMetadata)[];

      icidKeys.forEach(key => {
        delete basicMetadata[key];
      });
    }

    return {
      ...basicMetadata,
      ...trackTimeshift,
      'video.timeshiftAllowed': this.media.metadata.isStartOver
        ? 'true'
        : block.data.startOverAvailable?.toString() ?? 'false',
    } as AdobeMetadata;
  }

  onSeeking(): void {
    this.playbackHelper.onSeeking();
  }

  onDestroy(): void {
    this.playbackHelper.onDestroy();
  }

  onBuffering(): void {
    this.playbackHelper.onBuffering();
  }

  onPause(): void {
    if (this.media.metadata.isStartOver) {
      this.playbackHelper.onPause();
    } else {
      this.playbackHelper.onStop(true);
      this.currentBlock = undefined;
      this.lastSessionStartData = undefined;
      this.initiationType = 'manual';
    }
  }

  onStop(): void {
    this.playbackHelper.onStop();
  }
}

const getTimeshiftInformation = (
  initiationType: PlaybackInitTrackingTypes,
  duration: number,
  customData: { liveEdge: number; startTime: number }
) => {
  if (!isTimeshifted(initiationType)) return {};
  if (!customData || !duration || !customData.liveEdge || !customData.startTime) return {};
  // Duration is in ms
  const durationSeconds = Math.ceil(duration / 1000);
  const timeshiftSeconds = Math.ceil(customData.liveEdge - customData.startTime);
  const timeshiftPercent = timeshiftSeconds / durationSeconds;
  if (timeshiftPercent < 0 || timeshiftPercent > 1.05) return {};
  return {
    'video.timeshiftSeconds': timeshiftSeconds.toString(),
    'video.timeshiftPercent': timeshiftPercent.toFixed(1),
  };
};

const isTimeshifted = (initiationType: PlaybackInitTrackingTypes) =>
  (
    ['timeshifted', 'seek to timeshift', 'next-up timeshift'] as PlaybackInitTrackingTypes[]
  ).includes(initiationType);
